<template>
  <div>
    <v-card flat>
      <v-card-title class="d-flex align-center justify-space-between">
        <span>Patients ({{ patients.length }}/5)</span>
        <v-btn v-if="patients.length < 5" color="primary" small @click="openPatientDialog()">
          <v-icon small left>mdi-plus</v-icon>
          Add Patient
        </v-btn>
      </v-card-title>

      <v-card-text>
        <v-alert v-if="patients.length === 0" type="info" text> No patients added yet </v-alert>

        <v-data-table
          v-else
          :headers="headers"
          :items="patientsWithAbsentTime"
          :hide-default-footer="true"
          :disable-pagination="true"
          :disable-sort="true"
          class="elevation-1"
        >
          <template v-slot:item.name="{ item }">
            {{ computedName(item) }}
          </template>

          <template v-slot:item.birthday="{ item }">
            {{ formatBirthday(item.birthday) }}
          </template>

          <template v-slot:item.contactNumber="{ item }">
            {{ formatPhone(item.contactNumber) }}
          </template>

          <template v-slot:item.absentTime="{ item }">
            {{ item.absentTime !== null && item.absentTime !== undefined ? item.absentTime : 0 }}
          </template>

          <template v-slot:item.actions="{ item, index }">
            <v-icon small class="mr-2" @click="openPatientDialog(item, index)"> mdi-pencil </v-icon>
            <v-icon small color="error" @click="deletePatient(index)"> mdi-delete </v-icon>
          </template>
        </v-data-table>

        <v-chip v-if="patients.length >= 5" color="warning" small class="mt-3">
          Maximum 5 patients allowed
        </v-chip>
      </v-card-text>
    </v-card>

    <!-- Patient Dialog -->
    <v-dialog v-model="patientDialog" max-width="600px" persistent>
      <PatientForm
        v-if="patientDialog"
        :patient-data="selectedPatient"
        :is-edit="isEdit"
        @save="savePatient"
        @close="closePatientDialog"
      />
    </v-dialog>
  </div>
</template>

<script>
import PatientForm from './PatientForm';
import { getPatientAbsentTime } from '@/api/bookings';
import api from '@/api';

export default {
  name: 'PatientsList',
  components: {
    PatientForm,
  },
  props: {
    value: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      patientDialog: false,
      selectedPatient: null,
      selectedIndex: -1,
      isEdit: false,
      absentTimeData: [],
      headers: [
        { text: 'Patient Name', value: 'name' },
        { text: 'Birthday', value: 'birthday' },
        { text: 'Contact Number', value: 'contactNumber' },
        { text: 'Absent Time', value: 'absentTime' },
        { text: 'Actions', value: 'actions', align: 'end' },
      ],
    };
  },
  computed: {
    patients: {
      get() {
        return this.value || [];
      },
      set(val) {
        this.$emit('input', val);
      },
    },
    patientsWithAbsentTime() {
      return this.patients.map(patient => {
        const absentTimeInfo = this.absentTimeData.find(
          item => item.patientId === patient._id || item.patientId === String(patient._id),
        );
        return {
          ...patient,
          absentTime: absentTimeInfo ? absentTimeInfo.count : null,
        };
      });
    },
  },
  watch: {
    patients: {
      immediate: true,
      deep: true,
      handler(newPatients) {
        if (newPatients && newPatients.length > 0) {
          this.fetchAbsentTime(newPatients);
        } else {
          this.absentTimeData = [];
        }
      },
    },
  },
  methods: {
    async fetchAbsentTime(patients) {
      try {
        const patientIds = patients.map(p => p._id || p).filter(Boolean);
        if (patientIds.length === 0) {
          this.absentTimeData = [];
          return;
        }
        this.absentTimeData = await getPatientAbsentTime(api, patientIds);
      } catch (error) {
        console.error('Failed to fetch absent time:', error);
        this.absentTimeData = [];
      }
    },

    computedName(item) {
      const firstName = item.name_first || '';
      const lastName = item.name_last || '';
      return `${lastName} ${firstName}`.trim() || '-';
    },

    formatPhone(phone) {
      if (!phone) return '-';
      return phone;
    },

    formatBirthday(birthday) {
      if (!birthday) return '-';
      const date = new Date(birthday);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    },

    openPatientDialog(patient = null, index = -1) {
      this.selectedPatient = patient ? { ...patient } : null;
      this.selectedIndex = index;
      this.isEdit = !!patient;
      this.patientDialog = true;
    },

    closePatientDialog() {
      this.patientDialog = false;
      this.selectedPatient = null;
      this.selectedIndex = -1;
      this.isEdit = false;
    },

    savePatient(patientData) {
      const updatedPatients = [...this.patients];

      if (this.isEdit && this.selectedIndex >= 0) {
        // Update existing patient
        updatedPatients[this.selectedIndex] = patientData;
      } else {
        // Add new patient
        if (updatedPatients.length < 5) {
          updatedPatients.push(patientData);
        }
      }

      this.patients = updatedPatients;
      this.closePatientDialog();
      this.$emit('change');
    },

    deletePatient(index) {
      const patient = this.patients[index];
      const patientName = this.computedName(patient);

      this.$root.$confirm
        .open('Confirm Delete', `Are you sure you want to remove patient "${patientName}"?`, {
          color: 'red',
        })
        .then(async confirm => {
          if (confirm) {
            const updatedPatients = [...this.patients];
            updatedPatients.splice(index, 1);
            this.patients = updatedPatients;
            this.$emit('change');
          }
        });
    },
  },
};
</script>

<style scoped></style>
